import React, { useEffect, useMemo, useState } from 'react';
import kkpService from '../../services/kkpService';
import {
  filterStatutsForMagMonth,
  filterRefsForMagMonth,
  canonicalMagPeriod,
} from '../../utils/kkpMagPeriodUtils';
import { getKkpDefaultMagPeriod } from '../../utils/kkpDefaultPeriod';
import {
  KKP_VENTE_WIZARD_STEPS,
  briefCreatifToPayload,
  suggestBriefWizardStep,
  venteBriefFromApi,
} from '../../utils/kkpVenteBriefUtils';
import { briefPiecesJointesFromVente } from '../../utils/kkpBriefFichierUtils';
import KkpClientAutocomplete from './KkpClientAutocomplete';
import KkpVenteWizardSteps from './KkpVenteWizardSteps';
import KkpVenteBriefCreativeStep from './KkpVenteBriefCreativeStep';
import { confirmVenteDelete, confirmVenteClear } from '../../utils/confirmVenteDelete';
import useAppConfirm from '../../hooks/useAppConfirm';
import { showViewportNotice } from '../../utils/showViewportNotice';
import { getKkpSurfaceStyle } from '../../utils/kkpColorUtils';
import {
  AppButton,
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
  AppTextarea,
} from '../ui';
import {
  batValideOptionTitle,
  interceptGraphisteBatValideRequest,
  interceptLockedBatValideChange,
  isBatValideOptionLocked,
} from '../../utils/kkpBatValide';
import '../../styles/kkp-vente-wizard.scss';

const EMPTY_FORM = {
  annee: new Date().getFullYear(),
  mois: getKkpDefaultMagPeriod().mois,
  commercial_id: '',
  client_label: '',
  entreprise_locale_id: '',
  dolibarr_thirdparty_id: '',
  format: '1/4 PAGE',
  contenu: 'PUBLICITE',
  statut_phase: 'Option',
  commentaire: '',
  brief_creatif: '',
};

const CONTENU_TRAME = new Set(['COMMUNIQUE', 'PUB REPORTAGE']);

const hasContenuTrame = (contenu) => CONTENU_TRAME.has(String(contenu || '').toUpperCase().trim());

const KkpVenteMagModal = ({
  show,
  onClose,
  onSaved,
  onDeleted,
  onCleared,
  onError,
  meta,
  commerciaux = [],
  defaultCommercialId = '',
  initialValues = {},
  initialWizardStep,
  vente = null,
  onRequestBatValide,
  lockFormat = false,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [savedVente, setSavedVente] = useState(null);
  const [piecesJointes, setPiecesJointes] = useState([]);

  const activeVente = savedVente ?? vente;
  const activeVenteId = activeVente?.id ?? null;
  const isStructural = Boolean(activeVente?.is_mag_structural);
  const canDelete = activeVente?.can_delete ?? !activeVente?.is_mag_structural;
  const canClear = activeVente?.can_clear ?? Boolean(activeVenteId);
  const busy = saving || deleting || clearing;
  const { ask, modal } = useAppConfirm();

  const statutOptions = useMemo(
    () => filterStatutsForMagMonth(meta?.statuts, form.annee, form.mois),
    [meta?.statuts, form.annee, form.mois],
  );

  const formatOptions = useMemo(
    () => filterRefsForMagMonth(meta?.formats, form.annee, form.mois),
    [meta?.formats, form.annee, form.mois],
  );

  const contenuOptions = useMemo(
    () => filterRefsForMagMonth(meta?.contenus, form.annee, form.mois),
    [meta?.contenus, form.annee, form.mois],
  );

  const isFirst = currentStep === 0;
  const isLast = currentStep === KKP_VENTE_WIZARD_STEPS.length - 1;

  const selectedStatut = useMemo(
    () => statutOptions.find((s) => s.value === form.statut_phase),
    [statutOptions, form.statut_phase],
  );
  const statutSurface = useMemo(
    () => getKkpSurfaceStyle(selectedStatut?.couleur),
    [selectedStatut?.couleur],
  );
  const contenuHasTrame = hasContenuTrame(form.contenu);

  useEffect(() => {
    if (!show) return;

    setSavedVente(null);

    if (vente) {
      const period = canonicalMagPeriod(vente.annee, vente.mois);
      setForm({
        annee: period.annee,
        mois: period.mois,
        commercial_id: vente.commercial_id ?? '',
        client_label: vente.client_label,
        entreprise_locale_id: vente.entreprise_locale_id ?? '',
        dolibarr_thirdparty_id: vente.dolibarr_thirdparty_id ?? '',
        format: vente.format,
        contenu: vente.contenu,
        statut_phase: vente.statut_phase,
        commentaire: vente.commentaire ?? '',
        brief_creatif: venteBriefFromApi(vente.brief_encart),
      });
      setPiecesJointes(briefPiecesJointesFromVente(vente));
      setCurrentStep(suggestBriefWizardStep(vente, initialWizardStep));
    } else {
      const fallback = getKkpDefaultMagPeriod();
      const period = canonicalMagPeriod(
        initialValues.annee ?? new Date().getFullYear(),
        initialValues.mois ?? fallback.mois,
      );
      setForm({
        ...EMPTY_FORM,
        annee: period.annee,
        mois: period.mois,
        commercial_id: initialValues.commercial_id ?? defaultCommercialId ?? '',
        format: initialValues.format ?? formatOptions[0]?.value ?? meta?.formats?.[0]?.value ?? '1/4 PAGE',
        contenu: initialValues.contenu ?? contenuOptions[0]?.value ?? meta?.contenus?.[0]?.value ?? 'PUBLICITE',
        statut_phase: initialValues.statut_phase ?? filterStatutsForMagMonth(
          meta?.statuts,
          period.annee,
          period.mois,
        )[0]?.value ?? 'Option',
        ...initialValues,
        annee: period.annee,
        mois: period.mois,
        commercial_id: defaultCommercialId || '',
        brief_creatif: venteBriefFromApi(initialValues.brief_encart),
      });
      setPiecesJointes(briefPiecesJointesFromVente(initialValues));
      setCurrentStep(typeof initialWizardStep === 'number' ? initialWizardStep : 0);
    }
  }, [show, vente, initialValues, initialWizardStep, defaultCommercialId, meta]);

  useEffect(() => {
    if (!show || statutOptions.length === 0 || isStructural) return;
    if (!statutOptions.some((s) => s.value === form.statut_phase)) {
      setForm((f) => ({ ...f, statut_phase: statutOptions[0].value }));
    }
  }, [show, form.annee, form.mois, statutOptions, isStructural]);

  useEffect(() => {
    if (!show || formatOptions.length === 0 || isStructural) return;
    if (!formatOptions.some((f) => f.value === form.format)) {
      setForm((f) => ({ ...f, format: formatOptions[0].value }));
    }
  }, [show, form.annee, form.mois, formatOptions, isStructural]);

  useEffect(() => {
    if (!show || contenuOptions.length === 0) return;
    if (!contenuOptions.some((c) => c.value === form.contenu)) {
      setForm((f) => ({ ...f, contenu: contenuOptions[0].value }));
    }
  }, [show, form.annee, form.mois, contenuOptions]);

  const buildPayload = () => {
    const payload = {
      annee: form.annee,
      mois: form.mois,
      commercial_id: form.commercial_id || defaultCommercialId || null,
      client_label: form.client_label,
      entreprise_locale_id: form.entreprise_locale_id || null,
      dolibarr_thirdparty_id: form.dolibarr_thirdparty_id || null,
      contenu: form.contenu,
      statut_phase: form.statut_phase,
      commentaire: form.commentaire,
      brief_encart: briefCreatifToPayload(form.brief_creatif),
    };
    if (!isStructural) {
      payload.format = form.format;
    }
    return payload;
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (activeVenteId) {
        const { vente: updated, notice } = await kkpService.updateVenteMag(activeVenteId, payload);
        setSavedVente(updated);
        setPiecesJointes(briefPiecesJointesFromVente(updated));
        if (notice) {
          showViewportNotice(notice);
        }
        onSaved?.();
        onClose();
      } else {
        const { vente: created, notice } = await kkpService.createVenteMag(payload);
        setSavedVente(created);
        setPiecesJointes(briefPiecesJointesFromVente(created));
        if (notice) {
          showViewportNotice(notice);
        }
        onSaved?.();
        if (currentStep !== 1) {
          onClose();
        }
      }
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!activeVenteId) return;
    if (!(await confirmVenteClear(ask, activeVente?.client_label || form.client_label))) return;

    setClearing(true);
    try {
      const cleared = await kkpService.clearVenteMag(activeVenteId);
      setSavedVente(cleared);
      onCleared?.();
      onClose();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setClearing(false);
    }
  };

  const handleDelete = async () => {
    if (!activeVenteId) return;
    if (!(await confirmVenteDelete(ask, activeVente?.client_label || form.client_label))) return;

    setDeleting(true);
    try {
      await kkpService.deleteVenteMag(activeVenteId);
      onDeleted?.();
      onClose();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <AppModal
      show={show}
      title={activeVenteId ? 'Modifier la vente' : 'Nouvelle vente MAG'}
      onClose={onClose}
      size="lg"
      centered
      wrapBody={false}
      contentAs="form"
      contentProps={{ onSubmit: handleSave }}
    >

            <KkpVenteWizardSteps
              currentStep={currentStep}
              onStepChange={setCurrentStep}
            />

            <div className="kkp-vente-wizard-body">
              {currentStep === 0 && (
                <div className="row g-3">
                  <AppFormField className="col-md-6" label="Année" htmlFor="kkp-mag-annee" required>
                    <AppInput
                      id="kkp-mag-annee"
                      type="number"
                      required
                      value={form.annee}
                      onChange={(e) => setForm((f) => ({ ...f, annee: Number(e.target.value) }))}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Mois" htmlFor="kkp-mag-mois" required>
                    <AppSelect
                      id="kkp-mag-mois"
                      required
                      value={form.mois}
                      onChange={(e) => setForm((f) => ({ ...f, mois: Number(e.target.value) }))}
                    >
                      {(meta?.mois ?? []).map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-12" label="Client" required={!isStructural}>
                    <KkpClientAutocomplete
                      value={form.client_label}
                      onChange={(val) => setForm((f) => ({
                        ...f,
                        client_label: val,
                        entreprise_locale_id: '',
                        dolibarr_thirdparty_id: '',
                      }))}
                      onSelect={(client) => setForm((f) => ({
                        ...f,
                        client_label: client.nom ?? '',
                        dolibarr_thirdparty_id: Number(client?.id) > 0 ? Number(client.id) : '',
                        entreprise_locale_id: '',
                      }))}
                      required={!isStructural}
                    />
                  </AppFormField>
                  <AppFormField className="col-md-4" label="Statut" htmlFor="kkp-mag-statut" required={!isStructural}>
                    <AppSelect
                      id="kkp-mag-statut"
                      className="kkp-vente-field--statut"
                      required={!isStructural}
                      value={form.statut_phase}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (interceptLockedBatValideChange(next, form.statut_phase)) return;
                        if (interceptGraphisteBatValideRequest(next, form.statut_phase, activeVente, onRequestBatValide)) {
                          return;
                        }
                        setForm((f) => ({ ...f, statut_phase: next }));
                      }}
                      style={{
                        backgroundColor: statutSurface.backgroundColor,
                        color: statutSurface.color,
                      }}
                    >
                      {isStructural && <option value="">— Vide —</option>}
                      {statutOptions.map((s) => {
                        const optSurface = getKkpSurfaceStyle(s.couleur);
                        const locked = isBatValideOptionLocked(s.value);
                        return (
                          <option
                            key={s.value}
                            value={s.value}
                            title={batValideOptionTitle(s.value)}
                            style={{
                              backgroundColor: optSurface.backgroundColor,
                              color: locked ? '#9ca3af' : optSurface.color,
                            }}
                          >
                            {s.value}
                          </option>
                        );
                      })}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Format" htmlFor="kkp-mag-format" required>
                    <AppSelect
                      id="kkp-mag-format"
                      required
                      value={form.format}
                      disabled={isStructural || lockFormat}
                      onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
                    >
                      {formatOptions.map((f) => (
                        <option key={f.value} value={f.value}>{f.value}</option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-md-6" label="Contenu" htmlFor="kkp-mag-contenu" required>
                    <AppSelect
                      id="kkp-mag-contenu"
                      className={[
                        'kkp-vente-field--contenu',
                        contenuHasTrame ? 'kkp-vente-field--contenu-trame' : '',
                      ].filter(Boolean).join(' ')}
                      required
                      value={form.contenu}
                      onChange={(e) => setForm((f) => ({ ...f, contenu: e.target.value }))}
                    >
                      {contenuOptions.map((c) => (
                        <option
                          key={c.value}
                          value={c.value}
                          className={hasContenuTrame(c.value) ? 'kkp-vente-field--contenu-trame' : undefined}
                        >
                          {c.value}
                        </option>
                      ))}
                    </AppSelect>
                  </AppFormField>
                  <AppFormField className="col-12" label="Commentaire planning" htmlFor="kkp-mag-commentaire">
                    <AppTextarea
                      id="kkp-mag-commentaire"
                      rows={2}
                      placeholder="Notes internes (hors brief créatif)"
                      value={form.commentaire}
                      onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))}
                    />
                  </AppFormField>
                </div>
              )}

              {currentStep === 1 && (
                <KkpVenteBriefCreativeStep
                  value={form.brief_creatif}
                  onChange={(value) => setForm((f) => ({ ...f, brief_creatif: value }))}
                  venteId={activeVenteId}
                  piecesJointes={piecesJointes}
                  onPiecesJointesChange={setPiecesJointes}
                  onError={onError}
                />
              )}
            </div>

            <div className="kkp-vente-wizard-nav">
              <div className="d-flex gap-2">
                {activeVenteId && canClear && (
                  <AppButton
                    size="sm"
                    variant="outline-warning"
                    onClick={handleClear}
                    disabled={busy}
                    loading={clearing}
                    loadingLabel="Effacement…"
                  >
                    Effacer
                  </AppButton>
                )}
                {activeVenteId && canDelete && (
                  <AppButton
                    size="sm"
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={busy}
                    loading={deleting}
                    loadingLabel="Suppression…"
                  >
                    Supprimer
                  </AppButton>
                )}
              </div>
              <span className="kkp-vente-wizard-nav__progress text-secondary-light">
                Étape {currentStep + 1} / {KKP_VENTE_WIZARD_STEPS.length}
              </span>
              <div className="kkp-vente-wizard-nav__actions">
                <AppButton size="sm" variant="outline-secondary" onClick={onClose} disabled={deleting || clearing}>
                  Annuler
                </AppButton>
                {!isFirst && (
                  <AppButton
                    size="sm"
                    variant="outline-secondary"
                    icon="mdi:chevron-left"
                    onClick={() => setCurrentStep(0)}
                    disabled={busy}
                  >
                    Précédent
                  </AppButton>
                )}
                {currentStep === 1 && (
                  <AppButton
                    size="sm"
                    variant="link"
                    className="text-secondary-light text-decoration-none"
                    onClick={handleSave}
                    disabled={busy}
                  >
                    Compléter plus tard
                  </AppButton>
                )}
                <AppButton
                  type="submit"
                  size="sm"
                  variant="success"
                  disabled={busy}
                  loading={saving}
                  loadingLabel="Enregistrement…"
                >
                  Enregistrer
                </AppButton>
                {!isLast && (
                  <AppButton
                    size="sm"
                    variant="outline-primary"
                    iconClassName="ms-1"
                    onClick={() => setCurrentStep(1)}
                    disabled={busy}
                  >
                    Brief
                    <span className="ms-1" aria-hidden>
                      ›
                    </span>
                  </AppButton>
                )}
              </div>
            </div>
    </AppModal>
    {modal}
    </>
  );
};

export default KkpVenteMagModal;
